import { useState } from 'react'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import {
    ManagementAddNewLoan,
    ManagementAddNewLoanValidate,
    ManagementAddNewLoanValidateJustAdd,
    ManagementAddNewLoanValidateFuckingAdd,
} from 'services/loans/manager'
import { apiMessage } from 'modules/cashbox/ui'
import useFinishFlow, { FLOWS } from 'utilities/navigation/flow'
import { IGuarantorOption } from 'components/modals/guarantorPicker'
import { ITerms } from './steps/StepTerms'
import {
    isCustomSchedule,
    loanTermsPayload,
    resolveSchedule,
} from './useDraftSchedule'

export interface IGrantDraft {
    moneypoolId?: string
    fundName?: string
    borrower?: ILoanCandidate
    amount: string
    terms: ITerms
    guarantors: IGuarantorOption[]
    note: string
    card?: IBankAccountCards
    /** the Bale nudge this grant started from, if any — closes the funnel */
    nudgeId?: string | null
}

/**
 * Turning the wizard's answers into a loan.
 *
 * Two server calls, always in this order:
 *
 *  1. `draft-installments` asks the backend to build the schedule. The dates
 *     are Jalali-aware (month lengths differ, and a due date can land on a
 *     day the next month does not have), so the client must not invent them.
 *  2. the loan itself, carrying the schedule back — the server's own when
 *     the manager kept «مساوی», otherwise that draft re-cut to the chosen
 *     pattern or replaced by their hand edits (`resolveSchedule`, the same
 *     function the terms step previews with).
 *
 * A schedule that is not the server's own is checked with
 * `validate-draft-installments` before the loan is created. The create call
 * checks it too, but on failure it quietly falls back to the equal split and
 * still answers 201 — so without this, a rejected schedule would surface as
 * a loan with different instalments than the manager just approved.
 *
 * `loan_demand` is threaded through whenever the borrower had an open
 * request: that is what closes the request as accepted instead of leaving it
 * sitting in the queue next to the loan that answered it.
 */
export default function useGrantSubmit(
    draft: IGrantDraft,
    /** drop the saved wizard answers once the loan really exists */
    onGranted?: () => void
) {
    const finishFlow = useFinishFlow(FLOWS.grantLoan)
    const queryClient = useQueryClient()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const buildBase = () => {
        return {
            ...loanTermsPayload(draft.moneypoolId, draft.amount, draft.terms),
            poolShip: String(draft.borrower?.poolship_id ?? ''),
            tag: draft.note,
            loan_demand: draft.borrower?.demand?.id ?? null,
            guarantor_ids: draft.guarantors.map((item) => item.poolship_id),
        }
    }

    const submit = async (recordOnly: boolean) => {
        if (isSubmitting || !draft.borrower || !draft.moneypoolId) return
        setIsSubmitting(true)
        posthog.capture('loan_grant_submit_initiated', {
            fund_id: draft.moneypoolId,
            nudge_id: draft.nudgeId ?? null,
            record_only: recordOnly,
            amount: Number(draft.amount || 0),
            installments: Number(draft.terms.installments || 0),
            guarantors: draft.guarantors.length,
            has_card: !!draft.card,
            schedule_pattern: draft.terms.pattern ?? 'equal',
        })
        const base = buildBase()
        // which of the calls we are inside, so a failure names the one that
        // broke: a schedule the backend refused to build is a different bug
        // from a loan it refused to create off a schedule it just built
        let stage: 'draft_installments' | 'validate_schedule' | 'finalize' =
            'draft_installments'
        try {
            const drafted = await ManagementAddNewLoan(base as any)
            const schedule = resolveSchedule(
                drafted.installments,
                draft.amount,
                draft.terms
            )
            const installments = schedule.installments

            if (isCustomSchedule(schedule)) {
                stage = 'validate_schedule'
                const check = await ManagementAddNewLoanValidate({
                    ...(base as any),
                    installments,
                })
                if (!check?.is_valid) {
                    posthog.capture('loan_grant_failed', {
                        flow: 'wizard',
                        step: stage,
                        record_only: recordOnly,
                        fund_id: draft.moneypoolId,
                        schedule_pattern: schedule.pattern,
                        schedule_edited: schedule.edited,
                        amount_errors: check?.amount_errors?.length ?? 0,
                        due_date_errors: check?.due_date_errors?.length ?? 0,
                    })
                    toast.error(
                        check?.due_date_errors?.length
                            ? 'تاریخ اقساط پذیرفته نشد؛ جدول اقساط را بررسی کنید.'
                            : 'مبلغ اقساط پذیرفته نشد؛ جمع اقساط باید با مبلغ بازپرداخت یکی باشد.'
                    )
                    return
                }
            }
            stage = 'finalize'

            if (recordOnly) {
                await ManagementAddNewLoanValidateJustAdd({
                    ...(base as any),
                    installments,
                })
            } else {
                await ManagementAddNewLoanValidateFuckingAdd({
                    ...(base as any),
                    installments,
                    bankAccount: draft.card?.id,
                    ctx_id: Number(draft.moneypoolId),
                    ctx_type: 'mnp',
                    destination: 'mba',
                    is_group_pay: false,
                    payer_name: '',
                    receiver: draft.card?.member,
                    receiver_name: '',
                    source: 'hbb',
                    state: '',
                    state_time: '',
                    transaction_code: '',
                } as any)
            }

            posthog.capture('loan_granted', {
                // both this screen and the form it replaces emit `loan_granted`;
                // without this the two are indistinguishable and neither can be
                // measured against the other
                flow: 'wizard',
                record_only: recordOnly,
                fund_id: draft.moneypoolId,
                poolship_id: draft.borrower.poolship_id,
                amount: Number(draft.amount || 0),
                installments: Number(draft.terms.installments || 0),
                from_demand: !!draft.borrower.demand,
                demand_id: draft.borrower.demand?.id ?? null,
                score: draft.borrower.score,
                rank: draft.borrower.rank,
                guarantors: draft.guarantors.length,
                // how the repayment was spread (pwa#127): the pattern picked,
                // and whether any row was then changed by hand
                schedule_pattern: schedule.pattern,
                schedule_edited: schedule.edited,
                // The last step of the Bale nudge funnel. With this, «sent»
                // (server-side `loan_nudge_sent`), «opened»
                // (`loan_grant_started`, entry_source: bale_nudge) and
                // «granted» are three events on ONE person carrying ONE
                // nudge_id — so "did the notification lead to a loan?" is a
                // single PostHog funnel rather than a join against our tables.
                nudge_id: draft.nudgeId ?? null,
            })
            // the wizard said on every step that the request would close;
            // the confirmation says that it did
            toast.success(
                draft.borrower.demand
                    ? 'وام ثبت شد و درخواست وام عضو پذیرفته شد'
                    : 'وام با موفقیت ثبت شد'
            )
            // only here: a draft cleared on failure would lose everything the
            // manager typed at the exact moment they need to retry it
            onGranted?.()
            // the fund's balance, the request queue and every member list the
            // score is derived from all moved
            queryClient.invalidateQueries()
            // out of the wizard for good: back from the fund must not land on
            // a step whose draft was just cleared
            finishFlow(`/fund/${draft.moneypoolId}`, {
                poolShipId: draft.borrower.poolship_id,
            })
        } catch (error: any) {
            posthog.capture('loan_grant_failed', {
                flow: 'wizard',
                step: stage,
                record_only: recordOnly,
                fund_id: draft.moneypoolId,
                status: error?.response?.status,
            })
            const detail = error?.response?.data
            if (
                Array.isArray(detail) &&
                detail[0] === 'Interest cannot be more than 4%.'
            ) {
                toast.error('میزان کارمزد نباید بیش از ۴ درصد مبلغ وام باشد')
            } else {
                // A refusal with a reason — «موجودی نزد همیان صندوق … کافی
                // نیست» comes back as a 406 `detail` — is shown as is. Only a
                // body with nothing readable gets the generic line: «دوباره
                // تلاش کنید» is wrong advice for a fund that is short 11,408
                // tomans (2026-09-17, fund 32308).
                toast.error(
                    apiMessage(error, 'ثبت وام انجام نشد. دوباره تلاش کنید.')
                )
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    return { submit, isSubmitting }
}
